import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Heart, MessageSquareHeart, ExternalLink } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { hasFeedbackSubmitted, getParticipantId } from "@/lib/userInterviews";

/**
 * Hard lockout for tester accounts after 30 minutes of use.
 *
 * Trigger: account has `tester_code` AND `tester_used_at` is older than
 *          TESTER_SESSION_MINUTES (30) AND status is still approved.
 *
 * Behavior: full-screen modal (no close button) with a warm thank-you message
 *           and a single CTA → /feedback (the survey hard-locks them in until
 *           submission, and the backend then suspends the account so the link
 *           is single-use).
 *
 * Routes that bypass the gate: /feedback (so they can submit the survey) and
 * /study-complete (post-survey landing). Everything else is blocked.
 */

const TESTER_SESSION_MINUTES = 60;
const BYPASS_PATHS = ["/feedback", "/study-complete"];

function parseStartedAt(iso) {
    if (!iso) return null;
    const t = Date.parse(iso);
    return Number.isFinite(t) ? t : null;
}

export default function TesterLockoutGate() {
    const { user } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const iid = getParticipantId();

    const isTesterUser = !!(user && user !== false && user.role !== "admin" && user.tester_code && user.tester_code !== "demo-org-seed" && user.tester_code !== "org-test");
    // Timer starts from `study_started_at` (set when the tester actually
    // lands on /dashboard for the first time), NOT from `tester_used_at`
    // (set when the URL is first opened). Some testers click the link to
    // peek and only come back to use it later — those folks shouldn't get
    // locked out before they've even started.
    const startedAt = useMemo(
        () => (isTesterUser ? parseStartedAt(user?.study_started_at) : null),
        [isTesterUser, user]
    );
    const expiresAt = useMemo(
        () => (startedAt ? startedAt + TESTER_SESSION_MINUTES * 60 * 1000 : null),
        [startedAt]
    );

    // Re-evaluate "is it past expiry?" every 10 seconds. setInterval is plenty
    // for a 30-minute window — no need for sub-second precision.
    const [nowMs, setNowMs] = useState(() => Date.now());
    useEffect(() => {
        if (!isTesterUser || !expiresAt) return undefined;
        const id = setInterval(() => setNowMs(Date.now()), 10 * 1000);
        return () => clearInterval(id);
    }, [isTesterUser, expiresAt]);

    if (!isTesterUser) return null;
    if (!expiresAt) return null;
    if (nowMs < expiresAt) return null;

    // After feedback submission the tester is already done — let the redirect
    // chain (Feedback → /study-complete) happen normally.
    if (hasFeedbackSubmitted(iid)) return null;

    // Already on a route that's part of the wrap-up flow — don't block them.
    const onBypassPath = BYPASS_PATHS.some((p) => location.pathname.startsWith(p));
    if (onBypassPath) return null;

    const goToFeedback = () => {
        navigate("/feedback");
    };

    return (
        <div
            data-testid="tester-lockout-modal"
            className="fixed inset-0 z-[100] bg-[#1F2624]/85 backdrop-blur-sm flex items-start sm:items-center justify-center p-3 sm:p-6 overflow-y-auto"
            role="dialog"
            aria-modal="true"
            aria-labelledby="tester-lockout-title"
            style={{
                // Respect iOS safe areas so the bottom button isn't behind the
                // home indicator on iPhone, and the top isn't under the notch.
                paddingTop: "max(env(safe-area-inset-top, 0px), 12px)",
                paddingBottom: "max(env(safe-area-inset-bottom, 0px), 12px)",
            }}
        >
            <div
                className="bg-cream rounded-3xl shadow-2xl max-w-lg w-full p-5 sm:p-7 md:p-9 text-center my-auto max-h-full overflow-y-auto"
            >
                <div className="inline-flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 rounded-3xl bg-stage_late-light mb-4 sm:mb-5">
                    <Heart strokeWidth={1.6} size={24} className="text-stage_late-dark" />
                </div>
                <h2
                    id="tester-lockout-title"
                    className="text-xl sm:text-2xl md:text-3xl font-heading font-semibold tracking-tight text-[#12776F]"
                >
                    Thank you for testing Memory Lane.
                </h2>
                <p className="mt-3 sm:mt-4 text-sm sm:text-base text-[#4C5551] leading-relaxed">
                    Your hour with Memory Lane is up — and what you shared helps every caregiver who
                    lands here after you. The daughter searching at 2 AM. The son trying to
                    figure out hospice. They get a better tool because of you.
                </p>

                <button
                    type="button"
                    onClick={goToFeedback}
                    data-testid="tester-lockout-go-to-feedback"
                    className="mt-5 sm:mt-6 inline-flex items-center justify-center gap-2 bg-stage_late-dark hover:bg-stage_late-dark/90 active:bg-stage_late-dark/80 text-white rounded-2xl px-5 py-3.5 text-sm sm:text-base font-medium w-full min-h-[48px]"
                >
                    <MessageSquareHeart size={16} strokeWidth={1.8} />
                    Share your final thoughts (3 min survey)
                </button>

                <a
                    href="https://www.memorylanecare.net"
                    target="_blank"
                    rel="noreferrer"
                    data-testid="tester-lockout-request-access"
                    className="mt-3 inline-flex items-center justify-center gap-2 bg-white hover:bg-cream-dark active:bg-cream-dark/80 border border-[#E2E8F0] text-[#1F2624] rounded-2xl px-5 py-3 text-sm font-medium w-full min-h-[44px]"
                >
                    Back to memorylanecare.net
                    <ExternalLink size={13} strokeWidth={1.8} />
                </a>

                <p className="mt-3 text-xs text-[#4C5551] leading-snug">
                    The survey is the last step — it&apos;s how the next caregiver gets help.
                    If you&apos;d like full access, request it on memorylanecare.net.
                </p>
            </div>
        </div>
    );
}
